import React, { useState } from 'react';
import {
  Calendar, Clock, Edit3, Trash2, Mail, MapPin,
  DollarSign, FileText, User, Building,
  CheckCircle, XCircle, AlertCircle, Send, Search,
  Award, Target, MessageSquare, Loader, Snowflake
} from 'lucide-react';
import EditStepModal from './EditStepModal';
import { candidatesApi } from '../../services/api';
import { useSofteamboardStore } from '../../store/softeamboardStore';
import { ConfirmDialog } from '../shared/ConfirmDialog';
import Toast from '../ui/Toast';

const StepCard = ({ step, needId, candidateId, onStepUpdated, onStepDeleted, readOnly = false }) => {
  const { removeStepFromCache } = useSofteamboardStore();
  const [showEditModal, setShowEditModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [toast, setToast] = useState(null);

  // Step type configurations
  const stepTypeConfig = {
    'NO_CANDIDATE': { 
      icon: User, 
      color: 'bg-gray-100 text-gray-800 border-gray-200',
      bgColor: 'bg-gray-50',
      label: 'Sans candidat'
    },
    'CANDIDATE_IDENTIFIED': { 
      icon: Search, 
      color: 'bg-blue-100 text-blue-800 border-blue-200',
      bgColor: 'bg-blue-50',
      label: 'Candidat identifié'
    },
    'PUSH': { 
      icon: Send, 
      color: 'bg-indigo-100 text-indigo-800 border-indigo-200',
      bgColor: 'bg-indigo-50',
      label: 'CV proposé au client'
    },
    'CANDIDATE_SENT': { 
      icon: Send, 
      color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      bgColor: 'bg-yellow-50',
      label: 'Candidat envoyé'
    },
    'CANDIDATE_INTERVIEW': { 
      icon: Calendar, 
      color: 'bg-purple-100 text-purple-800 border-purple-200',
      bgColor: 'bg-purple-50',
      label: 'Entretien planifié'
    },
    'GO_NOGO': { 
      icon: AlertCircle, 
      color: 'bg-orange-100 text-orange-800 border-orange-200',
      bgColor: 'bg-orange-50',
      label: 'Décision GO / NO-GO'
    },
    'SALE': { 
      icon: CheckCircle, 
      color: 'bg-green-100 text-green-800 border-green-200',
      bgColor: 'bg-green-50',
      label: 'Vente finalisée'
    },
    'FROZEN': { 
      icon: Snowflake, 
      color: 'bg-cyan-100 text-cyan-800 border-cyan-200',
      bgColor: 'bg-cyan-50',
      label: 'En pause'
    },
    'CANCELED': { 
      icon: XCircle, 
      color: 'bg-red-100 text-red-800 border-red-200',
      bgColor: 'bg-red-50',
      label: 'Annulé'
    },
    'DELETED': { 
      icon: Trash2, 
      color: 'bg-gray-100 text-gray-600 border-gray-200',
      bgColor: 'bg-gray-50',
      label: 'Clôturé'
    }
  };

  // Determine if this step can be deleted (only detailed entity steps)
  const canDelete = () => {
    const entitySteps = ['CANDIDATE_SENT', 'CANDIDATE_INTERVIEW', 'GO_NOGO', 'SALE'];
    return entitySteps.includes(step.type);
  };

  // Check if this is a status update step
  const isStatusUpdate = () => {
    const statusSteps = ['NO_CANDIDATE', 'CANDIDATE_IDENTIFIED', 'CANCELED'];
    return statusSteps.includes(step.type);
  };

  const config = stepTypeConfig[step.type] || stepTypeConfig['NO_CANDIDATE'];
  const Icon = config.icon;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await candidatesApi.deleteStep(step.id);

      // Update store cache immediately
      removeStepFromCache(needId, candidateId, step.id);

      onStepDeleted?.(step.id);
      setToast({ message: 'Step deleted successfully', type: 'success' });
    } catch (error) {
      console.error('Error deleting step:', error);
      setToast({ message: `Failed to delete step: ${error.message}`, type: 'error' });
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const renderStepDetails = () => {
    switch (step.type) {
      case 'CANDIDATE_SENT':
        if (!step.detailCandidateSent) return null;
        return (
          <div className="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
            <h5 className="font-semibold text-yellow-800 mb-3 flex items-center">
              <Send size={16} className="mr-2" />
              Candidate Sent Details
            </h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div className="flex items-center">
                <Building size={14} className="mr-2 text-yellow-600" />
                <span className="font-medium">Client:</span>
                <span className="ml-1">{step.detailCandidateSent.clientName}</span>
              </div>
              <div className="flex items-center">
                <Mail size={14} className="mr-2 text-yellow-600" />
                <span className="font-medium">Email:</span>
                <span className="ml-1">{step.detailCandidateSent.clientEmail}</span>
              </div>
              <div className="flex items-center">
                <Calendar size={14} className="mr-2 text-yellow-600" />
                <span className="font-medium">Sent:</span>
                <span className="ml-1">{formatDate(step.detailCandidateSent.mailSentDate)}</span>
              </div>
              <div className="flex items-center">
                <Clock size={14} className="mr-2 text-yellow-600" />
                <span className="font-medium">Follow-up:</span>
                <span className="ml-1">{formatDate(step.detailCandidateSent.followUpDate)}</span>
              </div>
            </div>
          </div>
        );

      case 'CANDIDATE_INTERVIEW':
        if (!step.detailCandidateInterview) return null;
        return (
          <div className="mt-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
            <h5 className="font-semibold text-purple-800 mb-3 flex items-center">
              <Calendar size={16} className="mr-2" />
              Interview Details
            </h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div className="flex items-center">
                <Mail size={14} className="mr-2 text-purple-600" />
                <span className="font-medium">Invitation:</span>
                <span className="ml-1">{formatDate(step.detailCandidateInterview.invitationDate)}</span>
              </div>
              <div className="flex items-center">
                <Calendar size={14} className="mr-2 text-purple-600" />
                <span className="font-medium">Interview:</span>
                <span className="ml-1">{formatDate(step.detailCandidateInterview.interviewDate)}</span>
              </div>
              <div className="md:col-span-2 flex items-center">
                <MapPin size={14} className="mr-2 text-purple-600" />
                <span className="font-medium">Location:</span>
                <span className="ml-1">{step.detailCandidateInterview.interviewLocation}</span>
              </div>
            </div>
          </div>
        );

      case 'GO_NOGO':
        if (!step.detailGoNoGo) return null;
        return (
          <div className="mt-4 p-4 bg-orange-50 rounded-lg border border-orange-200">
            <h5 className="font-semibold text-orange-800 mb-3 flex items-center">
              <AlertCircle size={16} className="mr-2" />
              Go/No-Go Details
            </h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div className="flex items-center">
                <Target size={14} className="mr-2 text-orange-600" />
                <span className="font-medium">Decision:</span>
                <span className={`ml-1 px-2 py-1 rounded-full text-xs font-medium ${
                  step.detailGoNoGo.decision === 'GO' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {step.detailGoNoGo.decision}
                </span>
              </div>
              {step.detailGoNoGo.tjm && (
                <div className="flex items-center">
                  <DollarSign size={14} className="mr-2 text-orange-600" />
                  <span className="font-medium">TJM:</span>
                  <span className="ml-1">€{step.detailGoNoGo.tjm}</span>
                </div>
              )}
              {step.detailGoNoGo.projectedStartDate && (
                <div className="flex items-center">
                  <Calendar size={14} className="mr-2 text-orange-600" />
                  <span className="font-medium">Start Date:</span>
                  <span className="ml-1">{formatDate(step.detailGoNoGo.projectedStartDate)}</span>
                </div>
              )}
              {step.detailGoNoGo.endDate && (
                <div className="flex items-center">
                  <Calendar size={14} className="mr-2 text-orange-600" />
                  <span className="font-medium">End Date:</span>
                  <span className="ml-1">{formatDate(step.detailGoNoGo.endDate)}</span>
                </div>
              )}
              {step.detailGoNoGo.commercialConcession && (
                <div className="md:col-span-2 flex items-start">
                  <FileText size={14} className="mr-2 text-orange-600 mt-0.5" />
                  <span className="font-medium">Concession:</span>
                  <span className="ml-1">{step.detailGoNoGo.commercialConcession}</span>
                </div>
              )}
            </div>
          </div>
        );

      case 'SALE':
        if (!step.detailSale) return null;
        return (
          <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
            <h5 className="font-semibold text-green-800 mb-3 flex items-center">
              <CheckCircle size={16} className="mr-2" />
              Sale Details
            </h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div className="flex items-center">
                <DollarSign size={14} className="mr-2 text-green-600" />
                <span className="font-medium">TJM:</span>
                <span className="ml-1 font-semibold">€{step.detailSale.tjm}</span>
              </div>
              <div className="flex items-center">
                <FileText size={14} className="mr-2 text-green-600" />
                <span className="font-medium">Contract:</span>
                <span className="ml-1">{step.detailSale.contractType}</span>
              </div>
              <div className="flex items-center">
                <Calendar size={14} className="mr-2 text-green-600" />
                <span className="font-medium">Start:</span>
                <span className="ml-1">{formatDate(step.detailSale.startDate)}</span>
              </div>
              <div className="flex items-center">
                <Calendar size={14} className="mr-2 text-green-600" />
                <span className="font-medium">End:</span>
                <span className="ml-1">{formatDate(step.detailSale.endDate)}</span>
              </div>
              {step.detailSale.commercialConcession && (
                <div className="md:col-span-2 flex items-start">
                  <Award size={14} className="mr-2 text-green-600 mt-0.5" />
                  <span className="font-medium">Concession:</span>
                  <span className="ml-1">{step.detailSale.commercialConcession}</span>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <div className={`${config.bgColor} rounded-lg border-2 ${config.color.split(' ')[2]} p-4 mb-4 transition-all hover:shadow-md`}>
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-full ${config.color}`}>
              <Icon size={18} />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">{config.label}</h4>
              <p className="text-sm text-gray-600">{formatDate(step.date)}</p>
            </div>
          </div>
          
          {/* Action buttons */}
          <div className="flex items-center space-x-2">
            {/* ✅ UPDATED: Show edit button for all steps */}
            {!readOnly && (
              <button
                onClick={() => setShowEditModal(true)}
                className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                title="Edit step"
                aria-label="Edit step"
              >
                <Edit3 size={16} />
              </button>
            )}
            
            {/* Show delete button only for entity steps (detailed steps) */}
            {canDelete() && !readOnly && (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                disabled={deleting}
                className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors disabled:opacity-50"
                title="Delete step"
                aria-label="Delete step"
              >
                {deleting ? (
                  <Loader className="animate-spin" size={16} />
                ) : (
                  <Trash2 size={16} />
                )}
              </button>
            )}
            
            {/* Show info indicator for status updates */}
            {isStatusUpdate() && (
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-blue-500 rounded-full" title="Status Update"></div>
                 <span className="text-xs text-gray-500">Statut</span>
              </div>
            )}
          </div>
        </div>

        {/* Comment */}
        {step.comment && (
          <div className="mb-3 p-3 bg-white rounded-lg border border-gray-200">
            <div className="flex items-start">
              <MessageSquare size={14} className="mr-2 text-gray-500 mt-0.5" />
              <p className="text-sm text-gray-700 italic">"{step.comment}"</p>
            </div>
          </div>
        )}

        {/* Step-specific details */}
        {renderStepDetails()}

      </div>

      {/* ✅ UPDATED: Show edit modal for all steps */}
      {showEditModal && !readOnly && (
        <EditStepModal
          step={step}
          needId={needId}
          candidateId={candidateId}
          open={showEditModal}
          onClose={() => setShowEditModal(false)}
          onStepUpdated={onStepUpdated}
        />
      )}

      <ConfirmDialog
        open={showDeleteConfirm}
        title="Supprimer l'étape"
        message="Supprimer cette étape ? Cette action est irréversible."
        confirmLabel="Supprimer"
        variant="danger"
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default StepCard;
